/**
 * Icones de Klavox. Le jeu d'icones est celui du SOCLE d'IHM (`@santvia/ui-socle/icons`, CDC § « Le socle d'IHM est
 * commun a toute la flotte ») : une icone que le socle porte se PREND la, jamais redessinee ici. Restent locales,
 * listees dans `ICONES_LOCALES` avec leur raison, la marque Klavox et celles que le socle n’a pas encore -- elles se
 * DEMANDENT au socle (BACKLOG). Meme dessin que le socle : viewBox 24x24, trait 1,6, `currentColor`, zero emoji.
 */
import {
  ACCOUNT_TRANSFER_ICONS,
  AGENDA_ICONS,
  APP_LOCK_ICON,
  AUTH_SCREEN_ICON,
  COMPOSE_SEND_ICON,
  CONTACTS_ICONS,
  DRAFT_ICON,
  FEEDS_ICONS,
  FIND_IN_MESSAGE_ICONS,
  FOLDER_MANAGEMENT_ICONS,
  LIGHT_BACKGROUND_ICON,
  MESSAGE_LIST_ICONS,
  MESSAGE_LIST_MENU_ICONS,
  SEARCH_ICON,
  SETTINGS_SECTION_ICONS,
} from '@santvia/ui-socle/icons';

/**
 * Seules icones dessinees ici, et pourquoi. Toute autre icone exportee doit venir du socle
 * (`test/socle-ihm.test.ts` le verifie sur ce module meme).
 */
export const ICONES_LOCALES: Readonly<Record<string, string>> = {
  ICON_LOGO: 'marque Klavox (le socle ne porte aucune marque de produit)',
  ICON_STAR_FILLED: 'etoile PLEINE (favori actif) : le socle n’a que l’etoile au trait -- a demander',
  ICON_EYE: 'oeil (afficher un secret) : absent du socle -- a demander',
  ICON_EYE_OFF: 'oeil barre (masquer), pendant de ICON_EYE : le socle n’a qu’un oeil barre « traqueur bloque » -- a demander',
  ICON_CLOCK: 'horloge (code TOTP) : absente du socle -- a demander',
  ICON_MOON: 'lune (theme sombre), pendant du soleil du socle : absente -- a demander',
  ICON_FINGERPRINT: 'empreinte (passkey) : absente du socle -- a demander',
  ICON_CARD: 'carte bancaire : absente du socle -- a demander',
  ICON_IDENTITY: 'fiche d’identite : absente du socle -- a demander',
  ICON_CHECK: 'coche seule : absente du socle -- a demander',
  ICON_HELP: 'aide (point d’interrogation) : absente du socle -- a demander',
  ICON_LARGE_TYPE: 'grand format (un « A » dans un cadre agrandi) : le « volumineux » du socle est un poids, pas un texte -- a demander',
  ICON_TERMINAL: 'invite de terminal (cle SSH) : absente du socle -- a demander',
  ICON_WIFI: 'ondes Wi-Fi : le socle n’a que l’antenne « travailler en ligne » -- a demander',
  ICON_BANK: 'fronton a colonnes (compte bancaire) : absent du socle -- a demander',
  ICON_WALLET: 'portefeuille (crypto) : absent du socle -- a demander',
  ICON_DATABASE: 'cylindre (base de donnees) : absent du socle -- a demander',
  ICON_API_KEY: 'chevrons de code autour d’une cle (cle d’API) : absents du socle -- a demander',
  ICON_SOFTWARE_LICENSE: 'certificat scelle (licence logicielle) : absent du socle -- a demander',
  ICON_EMAIL_ACCOUNT: 'enveloppe d’un compte de messagerie : le socle n’a que la boite de reception -- a demander',
  ICON_MEMBERSHIP: 'carte de membre a buste : absente du socle -- a demander',
  ICON_MEDICAL: 'porte-documents a croix (dossier medical) : absent du socle -- a demander',
};

const SVG_ATTRS = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';

function svg(inner: string): string {
  return `<svg ${SVG_ATTRS} xmlns="http://www.w3.org/2000/svg">${inner}</svg>`;
}

/** Marque Klavox : deux chevrons imbriques (coffre/protection), utilisee aussi pour le favicon. */
export const ICON_LOGO = svg(
  '<path d="M5 5l6 7-6 7"/><path d="M13 5l6 7-6 7"/>',
);

export const ICON_SEARCH = SEARCH_ICON;
/** Trier la liste des entrees : les lignes decroissantes du menu de tri du socle. */
export const ICON_SORT = MESSAGE_LIST_MENU_ICONS.sort;
export const ICON_PLUS = AGENDA_ICONS.add;
export const ICON_STAR = FOLDER_MANAGEMENT_ICONS.favorite;
export const ICON_STAR_FILLED = svg(
  '<path d="M12 3.5l2.6 5.3 5.9.8-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.8z" fill="currentColor"/>',
);
export const ICON_EYE = svg(
  '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',
);
export const ICON_EYE_OFF = svg(
  '<path d="M3 3l18 18"/><path d="M10.6 5.2A10.6 10.6 0 0112 5c6.4 0 10 7 10 7a17.7 17.7 0 01-3.2 4.2M6.6 6.6C4 8.3 2 12 2 12s3.6 7 10 7a9.9 9.9 0 004-.8"/><path d="M9.9 9.9a3 3 0 004.2 4.2"/>',
);
/** Grand format : un « A » dans les quatre coins d'un cadre agrandi. */
export const ICON_LARGE_TYPE = svg(
  '<path d="M4 8V5a1 1 0 011-1h3M16 4h3a1 1 0 011 1v3M20 16v3a1 1 0 01-1 1h-3M8 20H5a1 1 0 01-1-1v-3"/><path d="M9 15.5l3-7.5 3 7.5M10.2 13h3.6"/>',
);
export const ICON_COPY = AGENDA_ICONS.duplicate;
/** Code QR de connexion d'un reseau Wi-Fi : le cadre de lecture du transfert par code QR du socle. */
export const ICON_QR = ACCOUNT_TRANSFER_ICONS.scan;
/** Dupliquer une entree : le meme dessin du socle, sous le nom de son geste. */
export const ICON_DUPLICATE = AGENDA_ICONS.duplicate;
export const ICON_LOCK = APP_LOCK_ICON;
export const ICON_FOLDER = FEEDS_ICONS.folder;
export const ICON_KEY = AUTH_SCREEN_ICON;
export const ICON_CLOCK = svg('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>');
export const ICON_DOWNLOAD = FOLDER_MANAGEMENT_ICONS.import;
export const ICON_UPLOAD = FOLDER_MANAGEMENT_ICONS.export;
export const ICON_SUN = LIGHT_BACKGROUND_ICON;
export const ICON_MOON = svg('<path d="M20 14.5A8.5 8.5 0 119.5 4a7 7 0 0010.5 10.5z"/>');
export const ICON_REFRESH = CONTACTS_ICONS.sync;
export const ICON_TRASH = MESSAGE_LIST_ICONS.delete;
export const ICON_ARCHIVE = MESSAGE_LIST_ICONS.archive;
export const ICON_LINK = AGENDA_ICONS.link;
/** Restaurer depuis la corbeille : la fleche retour du socle. */
export const ICON_RESTORE = MESSAGE_LIST_ICONS.reply;
export const ICON_SHIELD = SETTINGS_SECTION_ICONS.security!;
/** Passkey stockee dans une entree (R13) — empreinte, distincte du bouclier du deverrouillage. */
export const ICON_FINGERPRINT = svg(
  '<path d="M12 4a8 8 0 00-8 8v2"/><path d="M20 12a8 8 0 00-4-6.9"/><path d="M8 12a4 4 0 018 0v2a10 10 0 01-.7 3.7"/>' +
    '<path d="M12 12v3a13 13 0 01-1 5"/><path d="M6.5 19a9 9 0 001.5-5v-2"/>',
);
export const ICON_CHEVRON_RIGHT = AGENDA_ICONS.next;
export const ICON_CARD = svg(
  '<rect x="2.5" y="5" width="19" height="14" rx="2.2"/><path d="M2.5 9.5h19"/>',
);
export const ICON_NOTE = DRAFT_ICON;
export const ICON_IDENTITY = svg(
  '<circle cx="9" cy="9" r="3.2"/><path d="M4.5 19c1-3 3-4.5 4.5-4.5S13 16 14 19"/><path d="M16.5 8h4M16.5 12h4M16.5 16h3"/>',
);
export const ICON_PRINT = CONTACTS_ICONS.print;

/** Cle SSH (`ssh-key-item.ts`) : une invite de terminal, trait fin. */
export const ICON_TERMINAL = svg('<rect x="3" y="4.5" width="18" height="15" rx="3"/><path d="M7.5 9.5l3 2.5-3 2.5M13 15h3.5"/>');

/** Reseau Wi-Fi (`typed-fields.ts`) : trois ondes et un point, trait fin. */
export const ICON_WIFI = svg('<path d="M3 9.5a13 13 0 0118 0"/><path d="M6 13a8.5 8.5 0 0112 0"/><path d="M9 16.4a4 4 0 016 0"/><circle cx="12" cy="19.2" r=".9"/>');

/** Compte bancaire (`typed-fields.ts`) : un fronton a colonnes, trait fin. */
export const ICON_BANK = svg('<path d="M3.5 9.5L12 4.5l8.5 5"/><path d="M5 9.5h14"/><path d="M6.5 12v5M10 12v5M14 12v5M17.5 12v5"/><path d="M4 19.5h16"/>');

/** Portefeuille crypto (`typed-fields.ts`) : un portefeuille a rabat, trait fin. */
export const ICON_WALLET = svg('<path d="M4 7.5A2.5 2.5 0 016.5 5H17v3"/><rect x="4" y="7.5" width="16" height="11.5" rx="2.5"/><path d="M20 11.5h-3.5a1.75 1.75 0 000 3.5H20"/><circle cx="16.6" cy="13.25" r=".6"/>');

/** Base de donnees (`typed-fields.ts`) : un cylindre a trois etages, trait fin. */
export const ICON_DATABASE = svg('<ellipse cx="12" cy="5.5" rx="7" ry="2.5"/><path d="M5 5.5v13c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5v-13"/><path d="M5 12c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5"/>');

/** Cle d'API (`typed-fields.ts`) : chevrons de code autour d'une cle, trait fin. */
export const ICON_API_KEY = svg('<path d="M7.5 8L3.5 12l4 4"/><path d="M16.5 8l4 4-4 4"/><circle cx="10.25" cy="12" r="1.75"/><path d="M12 12h3.5"/><path d="M14.25 12v1.75"/>');

/** Licence logicielle (`typed-fields.ts`) : un certificat a coin plie, scelle d'un cachet a rubans, trait fin. */
export const ICON_SOFTWARE_LICENSE = svg('<path d="M11 20.5H6.5A1.5 1.5 0 015 19V5a1.5 1.5 0 011.5-1.5H14L18.5 8v3"/><path d="M14 3.5V8h4.5"/><path d="M8 10h5"/><path d="M8 13.5h3"/><circle cx="16" cy="15" r="2.5"/><path d="M14.6 17.1l-.6 3.4 2-1 2 1-.6-3.4"/>');

/** Serveur (`typed-fields.ts`) : deux unites de baie empilees, celles du serveur d'agenda du socle. */
export const ICON_SERVER = AGENDA_ICONS.server;

/** Compte de messagerie (`typed-fields.ts`) : une enveloppe ouverte sur un rabat, signe « @ » discret, trait fin. */
export const ICON_EMAIL_ACCOUNT = svg('<rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="M3.5 7l8.5 6 8.5-6"/><circle cx="17.5" cy="15.5" r="1.25"/>');

/** Carte de membre (`typed-fields.ts`) : une carte portant le buste du membre et deux lignes, trait fin. */
export const ICON_MEMBERSHIP = svg('<rect x="3" y="5" width="18" height="14" rx="2.5"/><circle cx="8.5" cy="10.75" r="2"/><path d="M5.5 16.25c.6-1.5 1.7-2.25 3-2.25s2.4.75 3 2.25"/><path d="M14 10h4"/><path d="M14 13.5h3"/>');

/** Dossier medical (`typed-fields.ts`) : un porte-documents frappe d'une croix, trait fin. */
export const ICON_MEDICAL = svg('<rect x="3.5" y="7" width="17" height="12.5" rx="2.5"/><path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7"/><path d="M12 10.5v5.5"/><path d="M9.25 13.25h5.5"/>');

export const TYPE_ICONS: Record<string, string> = {
  login: ICON_KEY,
  card: ICON_CARD,
  note: ICON_NOTE,
  identity: ICON_IDENTITY,
  'ssh-key': ICON_TERMINAL,
  wifi: ICON_WIFI,
  bank: ICON_BANK,
  wallet: ICON_WALLET,
  database: ICON_DATABASE,
  'api-key': ICON_API_KEY,
  'software-license': ICON_SOFTWARE_LICENSE,
  server: ICON_SERVER,
  email: ICON_EMAIL_ACCOUNT,
  membership: ICON_MEMBERSHIP,
  medical: ICON_MEDICAL,
};

export const ICON_PAPERCLIP = MESSAGE_LIST_ICONS.attachment;
export const ICON_FILE = AGENDA_ICONS.file;
export const ICON_CHECK = svg('<path d="M5 13l4 4L19 7"/>');
/** Etiquette d'entree (`entry-tags.ts`) : l'etiquette a oeillet du socle. */
export const ICON_TAG = MESSAGE_LIST_ICONS.tag;
/** Klavox Send : une trajectoire qui part, pas un avion en papier. */
export const ICON_SEND = COMPOSE_SEND_ICON;
export const ICON_HELP = svg(
  '<circle cx="12" cy="12" r="9"/><path d="M9.6 9.4a2.5 2.5 0 114 2.1c-.9.6-1.6 1.1-1.6 2.1"/>' +
    '<circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none"/>',
);
export const ICON_CLOSE = FIND_IN_MESSAGE_ICONS.close;
/** Ouvrir une adresse dans un nouvel onglet : la fleche qui sort du cadre du socle. */
export const ICON_EXTERNAL = FEEDS_ICONS.external;
export const ICON_ALERT = FEEDS_ICONS.warning;
/** Monter / descendre une adresse dans l'editeur : les chevrons haut / bas de la recherche du socle. */
export const ICON_MOVE_UP = FIND_IN_MESSAGE_ICONS.previous;
export const ICON_MOVE_DOWN = FIND_IN_MESSAGE_ICONS.next;
